import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import FullscreenImageViewer from "src/features/SocialFeedContent/components/FullscreenImageViewer/FullscreenImageViewer";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { UserCardHeader } from "src/features/SocialFeedContent/components";
import PostEngagementBar from "src/features/SocialFeedContent/components/PostEngagementBar/PostEngagementBar";
import CommentThread from "src/features/SocialFeedContent/components/CommentThread/CommentThread";
import ReactionsSheet from "src/features/SocialFeedContent/components/ReactionsSheet/ReactionsSheet";
import { useUser } from "src/hooks";
import PlayCircleIcon from "src/assets/icon/play_circle.svg?react";
import { VideoDownloadModal } from "src/features/SocialFeedContent/containers/VideoDownloadModal";
import { usePostComments } from "src/features/SocialFeedContent/hooks";
import dayjs from "dayjs";
import useVideoAssetByMediaId from "src/components/AssetUploaders/useVideoAssetByMediaId";
import assetClient from "src/services/assetClient";
import ImpersonationBanner from "src/components/ImpersonationBanner/ImpersonationBanner";

export interface SocialPostProps {
	id: string;
	authorId?: string | null;
	authorName?: string | null;
	authorRole?: string | null;
	authorAvatarUrl?: string | null;
	body?: string;
	images?: string[];
	videos?: string[] | null;
	hashtags?: Array<
		string | { uuid: string; name: string; is_private: boolean }
	>;
	isSponsored?: boolean;
	sponsorName?: string | null;
	publishedDate?: Date | string;
	commentsCount?: number;
	reactionsCount?: number;
	reactionTypes?: string[];
	expandComments?: boolean;
	bare?: boolean;
	onHashtagClick?: (hashtag: string) => void;
	impersonatedBy?: {
		firstName: string;
		lastName: string;
		avatarUrl: string | null;
	} | null;
}

const parseHashtagsMentions = (
	body?: string,
	onHashtagClick?: (hashtag: string) => void
): ReactNode => {
	if (!body) return null;

	const tokenRegex = /#\[(.*?)\]\((.*?)\)|@\[(.*?)\]\((.*?)\)|#(\w+)/g;
	const parts: ReactNode[] = [];
	let lastIndex = 0;
	let match;

	while ((match = tokenRegex.exec(body)) !== null) {
		if (match.index > lastIndex) {
			parts.push(body.slice(lastIndex, match.index));
		}

		if (match[1] !== undefined) {
			const hashtagName = match[1];
			parts.push(
				<span
					key={`hashtag-${match.index}`}
					role="button"
					tabIndex={0}
					className="text-primary font-medium cursor-pointer hover:underline"
					onClick={(e) => {
						e.stopPropagation();
						onHashtagClick?.(hashtagName);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							e.stopPropagation();
							onHashtagClick?.(hashtagName);
						}
					}}
				>
					#{hashtagName}
				</span>
			);
		} else if (match[3] !== undefined) {
			const mentionName = match[3];
			parts.push(
				<span key={`mention-${match.index}`} className="font-semibold">
					@{mentionName}
				</span>
			);
		} else if (match[5] !== undefined) {
			const plainHashtag = match[5];
			parts.push(
				<span
					key={`hashtag-${match.index}`}
					role="button"
					tabIndex={0}
					className="text-primary font-medium cursor-pointer hover:underline"
					onClick={(e) => {
						e.stopPropagation();
						onHashtagClick?.(plainHashtag);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							e.stopPropagation();
							onHashtagClick?.(plainHashtag);
						}
					}}
				>
					#{plainHashtag}
				</span>
			);
		}

		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < body.length) {
		parts.push(body.slice(lastIndex));
	}

	return <>{parts}</>;
};

/** Strip markdown tokens to get display length for truncation */
const stripMarkdownTokens = (text: string): string =>
	text.replace(/#\[(.*?)\]\(.*?\)|@\[(.*?)\]\(.*?\)/g, (_, h, m) =>
		h !== undefined ? `#${h}` : `@${m}`
	);

/** Truncate raw markdown body at a token-safe boundary */
const truncateBody = (body: string, maxDisplayLen: number): string => {
	const tokenRegex = /#\[(.*?)\]\(.*?\)|@\[(.*?)\]\(.*?\)/g;
	let displayLen = 0;
	let rawIdx = 0;
	let match;
	while ((match = tokenRegex.exec(body)) !== null) {
		const plainBefore = match.index - rawIdx;
		if (displayLen + plainBefore >= maxDisplayLen) {
			return body.substring(0, rawIdx + (maxDisplayLen - displayLen)) + "...";
		}
		displayLen += plainBefore;
		const hashtagName = match[1];
		const mentionName = match[2];
		const name =
			hashtagName !== undefined ? `#${hashtagName}` : `@${mentionName}`;
		if (displayLen + name.length > maxDisplayLen) {
			return body.substring(0, rawIdx + plainBefore) + "...";
		}
		displayLen += name.length;
		rawIdx = match.index + match[0].length;
		if (displayLen === maxDisplayLen) {
			return body.substring(0, rawIdx) + "...";
		}
	}
	const remaining = maxDisplayLen - displayLen;
	return body.substring(0, rawIdx + remaining) + "...";
};

async function resolveImageUrl(uuid: string, profile: string): Promise<string> {
	try {
		const { data } = await assetClient.get(
			`/api/v1.0/asset/image/${uuid}/all`,
			{ params: { profileName: profile } }
		);
		return data?.full || data?.default || "";
	} catch {
		return "";
	}
}

const SocialPost = ({
	id,
	authorId,
	authorName,
	authorRole,
	authorAvatarUrl,
	body,
	images = [],
	videos,
	hashtags = [],
	isSponsored,
	sponsorName,
	publishedDate,
	commentsCount = 0,
	reactionsCount = 0,
	reactionTypes = [],
	expandComments = false,
	bare = false,
	onHashtagClick,
	impersonatedBy
}: SocialPostProps) => {
	const needsUserFetch = !authorName && Boolean(authorId);
	const { data: user } = useUser(authorId, false, {
		enabled: needsUserFetch
	});

	// Lazy load: only fetch video when card is visible
	const cardRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const el = cardRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "200px" }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const { data: videoAsset, isInitialLoading: isLoadingVideoAsset } =
		useVideoAssetByMediaId({
			mediaId: videos && videos.length > 0 ? videos[0] : undefined,
			enabled: isVisible && Boolean(videos && videos.length > 0)
		});

	const [isVideoOpen, setIsVideoOpen] = useState(false);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [fullscreenIndex, setFullscreenIndex] = useState(-1);
	const [resolvedImages, setResolvedImages] = useState<
		Array<{ src: string; alt: string }>
	>([]);
	const resolvedImageUuids = useRef<string[]>([]);
	const [carouselIndex, setCarouselIndex] = useState(0);
	const carouselRef = useRef<HTMLDivElement>(null);
	const [isBodyExpanded, setIsBodyExpanded] = useState(false);
	const [isCommentsExpanded, setIsCommentsExpanded] = useState(expandComments);
	const [isReactionsOpen, setIsReactionsOpen] = useState(false);

	const {
		data: commentsData,
		hasNextPage: hasMoreComments,
		isFetchingNextPage: isLoadingMoreComments,
		fetchNextPage: loadMoreComments
	} = usePostComments(id, isCommentsExpanded);

	const videoUuid = videos && videos.length > 0 ? videos[0] : null;

	const handleCarouselScroll = () => {
		const el = carouselRef.current;
		if (!el || images.length === 0) return;
		const scrollLeft = el.scrollLeft;
		const itemWidth = el.scrollWidth / images.length;
		const index = Math.round(scrollLeft / itemWidth);
		setCarouselIndex(index);
	};

	const scrollToImage = (index: number) => {
		const el = carouselRef.current;
		if (!el) return;
		const itemWidth = el.scrollWidth / images.length;
		el.scrollTo({ left: itemWidth * index, behavior: "smooth" });
	};

	// Resolve full size URLs on demand when fullscreen viewer opens
	const openFullscreenViewer = async (startIndex: number) => {
		// If images are already cached for these exact UUIDs, open immediately
		const cachedUuids = resolvedImageUuids.current;
		if (
			cachedUuids.length === images.length &&
			cachedUuids.every((uuid, i) => uuid === images[i])
		) {
			setFullscreenIndex(startIndex);
			return;
		}

		// Resolve URLs first, then open
		const resolved = await Promise.all(
			images.map(async (uuid, i) => {
				const url = await resolveImageUrl(uuid, "retail-app-social-image");
				return {
					src: url,
					alt: `Post image ${i + 1} of ${images.length}`
				};
			})
		);
		resolvedImageUuids.current = [...images];
		setResolvedImages(resolved);
		setFullscreenIndex(startIndex);
	};

	const comments = commentsData?.pages.flatMap(({ results }) => results) ?? [];

	return (
		<>
			<div
				ref={cardRef}
				className={
					bare
						? "bg-white"
						: "rounded-xl border border-border bg-white shadow-sm hover:shadow-md hover:-translate-y-px transition-all duration-200"
				}
				data-testid="social-post"
			>
				{impersonatedBy && (
					<ImpersonationBanner
						impersonatedBy={{
							firstName: impersonatedBy.firstName,
							lastName: impersonatedBy.lastName,
							avatarUrl: impersonatedBy.avatarUrl
						}}
					/>
				)}
				<UserCardHeader
					userId={isSponsored ? null : authorId}
					name={isSponsored ? sponsorName : authorName || user?.name}
					role={isSponsored ? "Sponsored Post" : authorRole || user?.role}
					avatarUrl={isSponsored ? null : authorAvatarUrl}
					timestamp={
						publishedDate
							? dayjs(publishedDate as Date).format("DD MMM YYYY")
							: undefined
					}
				/>

				{body && (
					<div className="px-4 pb-3">
						<p className="text-sm text-foreground">
							{isBodyExpanded || stripMarkdownTokens(body).length <= 150
								? parseHashtagsMentions(body, onHashtagClick)
								: parseHashtagsMentions(
										truncateBody(body, 150),
										onHashtagClick
									)}
						</p>
						{stripMarkdownTokens(body).length > 150 && (
							<button
								type="button"
								className="text-sm font-medium text-muted-foreground hover:text-foreground mt-1"
								onClick={(e) => {
									e.stopPropagation();
									setIsBodyExpanded(!isBodyExpanded);
								}}
							>
								{isBodyExpanded ? "Show less" : "Read more"}
							</button>
						)}
					</div>
				)}

				{hashtags.length > 0 && (
					<div className="px-4 pb-3 flex flex-wrap gap-1.5">
						{hashtags.map((tag) => {
							const name = typeof tag === "string" ? tag : tag.name;
							return (
								<span
									key={name}
									role="button"
									tabIndex={0}
									className="inline-flex items-center rounded-full bg-primary/10 text-primary text-xs px-2.5 py-0.5 font-medium cursor-pointer hover:bg-primary/20 transition-colors"
									onClick={(e) => {
										e.stopPropagation();
										onHashtagClick?.(name);
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											e.stopPropagation();
											onHashtagClick?.(name);
										}
									}}
								>
									#{name}
								</span>
							);
						})}
					</div>
				)}

				{images.length > 0 && isVisible && (
					<div className="relative">
						<div
							className="flex items-stretch gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-4 pb-3"
							ref={carouselRef}
							onScroll={handleCarouselScroll}
						>
							{images.map((uuid, i) => (
								<div
									key={uuid}
									className="snap-center shrink-0 first:pl-0 last:pr-0"
									style={{
										width: images.length === 1 ? "100%" : "85%"
									}}
								>
									<div
										role="button"
										tabIndex={0}
										aria-label={`View image ${i + 1} of ${images.length} full size`}
										onClick={(e) => {
											e.stopPropagation();
											openFullscreenViewer(i);
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												openFullscreenViewer(i);
											}
										}}
										className="cursor-pointer rounded-xl overflow-hidden bg-neutral-100 h-full flex items-center justify-center"
									>
										<AzureImage
											uuid={uuid}
											imageProfile="retail-app-social-image"
											alt={`Post image ${i + 1} of ${images.length}`}
											style={{
												display: "block",
												maxWidth: "100%",
												maxHeight: 600,
												width: "auto",
												height: "auto",
												objectFit: "contain"
											}}
										/>
									</div>
								</div>
							))}
						</div>

						{/* Arrow controls for multi image */}
						{images.length > 1 && carouselIndex > 0 && (
							<button
								type="button"
								aria-label="Previous image"
								onClick={(e) => {
									e.stopPropagation();
									scrollToImage(carouselIndex - 1);
								}}
								className="absolute left-6 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white transition-colors z-10"
							>
								<CaretLeft size={16} weight="bold" />
							</button>
						)}
						{images.length > 1 && carouselIndex < images.length - 1 && (
							<button
								type="button"
								aria-label="Next image"
								onClick={(e) => {
									e.stopPropagation();
									scrollToImage(carouselIndex + 1);
								}}
								className="absolute right-6 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white transition-colors z-10"
							>
								<CaretRight size={16} weight="bold" />
							</button>
						)}

						{/* Dot indicators for multi image */}
						{images.length > 1 && (
							<div className="flex justify-center gap-1.5 pb-2">
								{images.map((_, i) => (
									<button
										key={i}
										type="button"
										aria-label={`Go to image ${i + 1}`}
										aria-current={i === carouselIndex ? "true" : undefined}
										onClick={(e) => {
											e.stopPropagation();
											scrollToImage(i);
										}}
										className={[
											"rounded-full transition-all",
											i === carouselIndex
												? "w-2 h-2 bg-foreground"
												: "w-1.5 h-1.5 bg-foreground/30"
										].join(" ")}
									/>
								))}
							</div>
						)}
					</div>
				)}

				{images.length > 0 && !isVisible && (
					<div className="px-4 pb-3">
						<div className="rounded-xl bg-subtle" style={{ height: 200 }} />
					</div>
				)}

				{isVisible && isLoadingVideoAsset && (
					<div className="w-full bg-subtle aspect-video flex justify-center items-center">
						<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
					</div>
				)}

				{isVisible && videoUuid && videoAsset && (
					<div className="px-4 pb-3">
						<div className="relative rounded-xl overflow-hidden">
							{isVideoPlaying ? (
								<video
									src={videoAsset.playbackUri}
									poster={videoAsset.thumbnail}
									controls
									autoPlay
									aria-label="Post video"
									className="w-full rounded-xl"
									style={{ maxHeight: 400 }}
								/>
							) : (
								<button
									onClick={(e) => {
										e.stopPropagation();
										setIsVideoPlaying(true);
									}}
									aria-label="Play video"
									className="relative w-full"
								>
									<img
										src={videoAsset.thumbnail}
										alt="Video thumbnail"
										style={{
											display: "block",
											width: "100%",
											maxHeight: 400,
											objectFit: "cover",
											cursor: "pointer"
										}}
									/>
									<PlayCircleIcon
										color="white"
										style={{
											position: "absolute",
											width: 48,
											height: 48,
											top: "50%",
											left: "50%",
											transform: "translate(-50%, -50%)",
											filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
										}}
									/>
								</button>
							)}
						</div>
					</div>
				)}

				<PostEngagementBar
					reactionsCount={reactionsCount}
					reactionTypes={reactionTypes}
					commentsCount={commentsCount}
					isExpanded={isCommentsExpanded}
					onToggleComments={() => setIsCommentsExpanded((prev) => !prev)}
					onOpenReactions={() => setIsReactionsOpen(true)}
				/>

				{isCommentsExpanded && (
					<CommentThread
						comments={comments}
						hasMore={hasMoreComments ?? false}
						isLoadingMore={isLoadingMoreComments}
						onLoadMore={() => loadMoreComments()}
					/>
				)}
			</div>

			<FullscreenImageViewer
				isOpen={fullscreenIndex >= 0}
				images={resolvedImages}
				initialIndex={fullscreenIndex}
				onClose={() => setFullscreenIndex(-1)}
			/>

			<VideoDownloadModal
				video={videoAsset}
				videoUuid={videoUuid}
				isOpen={isVideoOpen}
				onClose={() => setIsVideoOpen(false)}
			/>

			<ReactionsSheet
				postId={id}
				isOpen={isReactionsOpen}
				onClose={() => setIsReactionsOpen(false)}
			/>
		</>
	);
};

export default SocialPost;
